import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import {
  getVersionNode,
  type VersionNodeDetail,
  type VersionNodeSummary,
} from '../../services/cplex';
import type { MPPRow } from './dailyView/types';
import {
  computeDeltasFromSummaries,
  formatObjective,
  formatPct,
  formatPerDay,
} from './versionDeltas';
import {
  diffPlans,
  networksIn,
  type OrderChange,
  type PlanDiff,
} from './versionPlanDiff';
import { VersionMetaList } from './VersionMetaList';

// Compare any two model variants (a "before" and an "after"). Shows the
// per-network objective/orders overview plus a plain-language breakdown of what
// actually changed in the production plan — which orders got newly filled,
// dropped, or moved to a different day, and where we make more/less material.
// Both versions' plans are fetched on demand and diffed client-side.

interface Props {
  versions: VersionNodeSummary[];
  initialBeforeId: string | null;
  initialAfterId: string;
  onClose: () => void;
}

const OVERLAY: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
};
const CARD: CSSProperties = {
  background: '#fff',
  color: '#1f2937',
  borderRadius: 12,
  width: 'min(820px, 95vw)',
  maxHeight: '88vh',
  overflow: 'auto',
  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
};
const TH: CSSProperties = {
  textAlign: 'right',
  padding: '6px 10px',
  fontSize: 12,
  color: '#64748b',
  borderBottom: '1px solid #cbd5e1',
};
const TD: CSSProperties = {
  textAlign: 'right',
  padding: '6px 10px',
  fontSize: 13,
  borderBottom: '1px solid #eef2f7',
};
const TD_LEFT: CSSProperties = { ...TD, textAlign: 'left', fontWeight: 600 };
const SECTION_H: CSSProperties = { fontSize: 13, fontWeight: 700, margin: '16px 0 6px' };
const PICKER: CSSProperties = { fontSize: 12, padding: '3px 6px', maxWidth: 240 };

function deltaColor(p: number | null): string {
  if (p === null || p === 0) return '#64748b';
  return p > 0 ? '#16a34a' : '#b45309';
}
function lbs(n: number): string {
  return Math.round(n).toLocaleString();
}
function mppRows(detail: VersionNodeDetail | null): MPPRow[] | null {
  const raw = detail?.result_tables?.master_production_plan as unknown;
  return Array.isArray(raw) ? (raw as MPPRow[]) : null;
}

export function VersionDeltaModal({ versions, initialBeforeId, initialAfterId, onClose }: Props) {
  const [beforeId, setBeforeId] = useState<string>(initialBeforeId ?? '');
  const [afterId, setAfterId] = useState<string>(initialAfterId);
  const [cache, setCache] = useState<Record<string, VersionNodeDetail>>({});
  const [networkSel, setNetworkSel] = useState<string>('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch the detail (incl. result_tables) for whichever versions are selected.
  useEffect(() => {
    const need = [beforeId, afterId].filter((id): id is string => !!id && !cache[id]);
    if (!need.length) return;
    let cancel = false;
    void Promise.all(
      need.map((id) =>
        getVersionNode(id)
          .then((d) => [id, d] as const)
          .catch(() => null),
      ),
    ).then((res) => {
      if (cancel) return;
      setCache((c) => {
        const n = { ...c };
        for (const r of res) if (r) n[r[0]] = r[1];
        return n;
      });
    });
    return () => {
      cancel = true;
    };
  }, [beforeId, afterId, cache]);

  const before = beforeId ? cache[beforeId] ?? null : null;
  const after = afterId ? cache[afterId] ?? null : null;
  // Constraint lists come from the lightweight version summaries (no fetch wait).
  const beforeSummary = versions.find((v) => v.id === beforeId) ?? null;
  const afterSummary = versions.find((v) => v.id === afterId) ?? null;

  const overview = useMemo(
    () => computeDeltasFromSummaries(before?.result_summary, after?.result_summary),
    [before, after],
  );
  const beforeRows = useMemo(() => mppRows(before), [before]);
  const afterRows = useMemo(() => mppRows(after), [after]);
  const networks = useMemo(
    () => networksIn(beforeRows ?? [], afterRows ?? []),
    [beforeRows, afterRows],
  );

  // The diffed network: the user's pick if still valid, else the headline
  // (primary) network, else the first available — derived, not stored, so we
  // don't setState inside an effect.
  const network = useMemo(() => {
    if (networkSel && networks.includes(networkSel)) return networkSel;
    const primary = overview?.primaryNetwork;
    if (primary && networks.includes(primary)) return primary;
    return networks[0] ?? '';
  }, [networkSel, networks, overview?.primaryNetwork]);

  const diff = useMemo<PlanDiff | null>(
    () => (beforeRows && afterRows && network ? diffPlans(beforeRows, afterRows, network) : null),
    [beforeRows, afterRows, network],
  );

  const bothChosen = !!beforeId && !!afterId;
  const detailReady = bothChosen && !!before && !!after;
  const loading = (!!beforeId && !cache[beforeId]) || (!!afterId && !cache[afterId]);

  return (
    <div
      style={OVERLAY}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Compare model variants"
    >
      <div style={CARD} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            background: '#fff',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>Compare variants</strong>
            <label style={{ fontSize: 11, color: '#64748b' }}>
              Before{' '}
              <select value={beforeId} onChange={(e) => setBeforeId(e.target.value)} style={PICKER}>
                <option value="">— select —</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label} [{v.state}]
                  </option>
                ))}
              </select>
            </label>
            <span style={{ color: '#94a3b8' }}>→</span>
            <label style={{ fontSize: 11, color: '#64748b' }}>
              After{' '}
              <select value={afterId} onChange={(e) => setAfterId(e.target.value)} style={PICKER}>
                <option value="">— select —</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label} [{v.state}]
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16 }}>
          {!bothChosen ? (
            <p style={{ fontSize: 13, color: '#64748b' }}>
              Pick a <strong>Before</strong> and an <strong>After</strong> version to compare.
            </p>
          ) : loading && !detailReady ? (
            <p style={{ fontSize: 13, color: '#64748b' }}>Loading the two plans…</p>
          ) : (
            <>
              {/* ---- objective / orders overview ---- */}
              {overview ? (
                <>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                    {overview.networksImproved !== null && overview.networksTotal !== null ? (
                      <Chip
                        label="Networks improved"
                        value={`${overview.networksImproved}/${overview.networksTotal}`}
                      />
                    ) : null}
                    {overview.objectivePct !== null ? (
                      <Chip
                        label={`Objective${overview.primaryNetwork ? ` (${overview.primaryNetwork})` : ''}`}
                        value={formatPct(overview.objectivePct)}
                        color={deltaColor(overview.objectivePct)}
                      />
                    ) : null}
                    {overview.totalOrdersFilledDelta !== null ? (
                      <Chip
                        label="Total orders filled Δ"
                        value={`${overview.totalOrdersFilledDelta >= 0 ? '+' : ''}${overview.totalOrdersFilledDelta}`}
                        color={deltaColor(overview.totalOrdersFilledDelta)}
                      />
                    ) : null}
                  </div>

                  {overview.perNetwork.length ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...TH, textAlign: 'left' }}>network</th>
                          <th style={TH}>objective (before → after)</th>
                          <th style={TH}>Δ</th>
                          <th style={TH}>orders</th>
                          <th style={TH}>day-spread</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.perNetwork.map((n) => (
                          <tr key={n.network}>
                            <td style={TD_LEFT}>{n.network}</td>
                            <td style={TD}>
                              {formatObjective(n.beforeObjective)} → {formatObjective(n.afterObjective)}
                            </td>
                            <td style={{ ...TD, color: deltaColor(n.objectivePct), fontWeight: 700 }}>
                              {formatPct(n.objectivePct)}
                            </td>
                            <td style={TD}>
                              {n.beforeOrders === null || n.afterOrders === null
                                ? '—'
                                : `${n.beforeOrders}→${n.afterOrders}`}
                            </td>
                            <td style={TD}>
                              {formatPerDay(n.beforePerDay)} → {formatPerDay(n.afterPerDay)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </>
              ) : (
                <p style={{ fontSize: 13, color: '#64748b' }}>
                  These two versions share no comparable solve summary.
                </p>
              )}

              {/* ---- constraints / assumptions per version ---- */}
              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 4 }}>
                <div style={SECTION_H}>Constraints &amp; assumptions</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}
                    >
                      Before — {beforeSummary?.label ?? '—'}
                    </div>
                    <VersionMetaList dynamicMetadata={beforeSummary?.dynamic_metadata} />
                  </div>
                  <div>
                    <div
                      style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}
                    >
                      After — {afterSummary?.label ?? '—'}
                    </div>
                    <VersionMetaList dynamicMetadata={afterSummary?.dynamic_metadata} />
                  </div>
                </div>
              </div>

              {/* ---- order- & material-level breakdown ---- */}
              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, ...SECTION_H }}>
                  <span>What changed in the plan</span>
                  {networks.length > 1 ? (
                    <select
                      value={network}
                      onChange={(e) => setNetworkSel(e.target.value)}
                      style={{ fontSize: 12, padding: '2px 6px' }}
                    >
                      {networks.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  ) : network ? (
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                      ({network})
                    </span>
                  ) : null}
                </div>

                {!beforeRows || !afterRows ? (
                  <p style={{ fontSize: 13, color: '#64748b' }}>
                    The detailed order/material breakdown needs a stored production plan on both
                    versions; one is missing here (e.g. a base/root version).
                  </p>
                ) : diff && diff.empty ? (
                  <p style={{ fontSize: 13, color: '#64748b' }}>No plan rows for this network.</p>
                ) : diff ? (
                  <>
                    <ProductionByDay diff={diff} />
                    <OrderList
                      title="Orders newly filled"
                      color="#16a34a"
                      rows={diff.newlyFilled}
                      render={(o) => `now on day ${o.toDay ?? '—'} · ${lbs(o.lbs)} lbs`}
                    />
                    <OrderList
                      title="Orders no longer filled"
                      color="#b91c1c"
                      rows={diff.noLongerFilled}
                      render={(o) => `was day ${o.fromDay ?? '—'} · ${lbs(o.lbs)} lbs dropped`}
                    />
                    <OrderList
                      title="Orders moved to a different day"
                      color="#b45309"
                      rows={diff.movedDay}
                      render={(o) =>
                        `day ${o.fromDay ?? '—'} → day ${o.toDay ?? '—'} · ${lbs(o.lbs)} lbs`
                      }
                    />
                    <MaterialChanges diff={diff} />
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      style={{
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '6px 12px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 11, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color ?? '#1f2937' }}>{value}</div>
    </div>
  );
}

function ProductionByDay({ diff }: { diff: PlanDiff }) {
  if (!diff.productionByDay.length) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, margin: '8px 0 4px' }}>
        Production by day (lbs)
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left' }}>day</th>
            <th style={TH}>before</th>
            <th style={TH}>after</th>
            <th style={TH}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {diff.productionByDay.map((d) => (
            <tr key={d.day}>
              <td style={TD_LEFT}>Day {d.day}</td>
              <td style={TD}>{lbs(d.parentLbs)}</td>
              <td style={TD}>{lbs(d.childLbs)}</td>
              <td style={{ ...TD, color: deltaColor(d.delta), fontWeight: 700 }}>
                {d.delta >= 0 ? '+' : ''}
                {lbs(d.delta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MaterialChanges({ diff }: { diff: PlanDiff }) {
  if (!diff.materialChanges.length) return null;
  return (
    <>
      <div style={SECTION_H}>Material changes (more / less)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left' }}>product (SKU)</th>
            <th style={TH}>before → after (lbs)</th>
            <th style={TH}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {diff.materialChanges.map((m) => (
            <tr key={m.item}>
              <td style={TD_LEFT}>{m.item}</td>
              <td style={TD}>
                {lbs(m.parentLbs)} → {lbs(m.childLbs)}
              </td>
              <td style={{ ...TD, color: deltaColor(m.delta), fontWeight: 700 }}>
                {m.delta >= 0 ? '+' : ''}
                {lbs(m.delta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const CAP = 15;

function OrderList({
  title,
  color,
  rows,
  render,
}: {
  title: string;
  color: string;
  rows: OrderChange[];
  render: (o: OrderChange) => string;
}) {
  if (!rows.length) return null;
  const shown = rows.slice(0, CAP);
  return (
    <div>
      <div style={{ ...SECTION_H, color }}>
        {title} ({rows.length})
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {shown.map((o) => (
          <li key={o.order} style={{ fontSize: 13, lineHeight: 1.5 }}>
            <strong>{o.customer || o.order}</strong>
            {o.au ? <span style={{ color: '#64748b' }}> ({o.au})</span> : null} — {render(o)}
          </li>
        ))}
      </ul>
      {rows.length > CAP ? (
        <div style={{ fontSize: 12, color: '#64748b', paddingLeft: 18 }}>
          + {rows.length - CAP} more
        </div>
      ) : null}
    </div>
  );
}
